// 4-step Influencer signup wizard for InfluenceHub MVP
// Step 1: Account creation (email, password, phone, first name, last name) - creates Firebase Auth user
// Step 2: Audience info (followers, niches, engagement, social links) 
// Step 3: Bio + profile picture
// Step 4: Profile preview and confirmation - final Firestore write
// Matches Figma frames with left logo panel and right form panel

/*
QA Test Cases:
- Select role → /signup/wizard → Step 1 creates account → Step 2 → Step 3 → Step 4 → Confirm writes onboardingComplete + profile
- Refresh during Steps 2–4 restores draft from "ih.wizardDraft.{uid}"
- "Email already in use" on Step 1 shows friendly error with link to /login
- If user not authenticated → render Step 1 (account). Steps 2–4 are blocked until Step 1 completes
- If authenticated and userType !== "influencer" → redirect /dashboard
- If authenticated and onboardingComplete === true → redirect /dashboard
- Wizard shows only Back/Next (steps 1–3) and no navigation on Step 4 (Preview handles its own buttons)
- No Save & Exit button or code path remains
- Refresh mid-step still restores draft (if autosave present)
- Step 4 Preview shows composed profile card with avatar, name, bio, niches, stats
- Clicking Back on Preview returns to Step 3 with values intact
- Confirm on Preview writes onboardingComplete + profile object to Firestore, clears draft, navigates to /dashboard
- Missing required fields on Preview disables Confirm and shows guidance
- Refresh on Preview still shows the same preview (state restored from localStorage)
- Visit /select-role → choose Influencer → navigates to /signup/wizard → loads Step 1 (account creation)
- Refresh mid-wizard → restores saved progress and current step from localStorage (if any)
- Signing up fresh always starts at Step 1, never Step 2
- After finishing Step 1, advances properly to Step 2
- From /select-role → choose Influencer → navigates to /signup/wizard?fresh=1 → Step 1 shows EMPTY inputs, NO banner, user is NOT authenticated
- Fill Step 1 → submit → user is created → move to Step 2
- Refresh on Step 2, 3, or 4 → draft restores from "ih.wizardDraft.{uid}"
- Visiting /signup/wizard without "fresh=1" still allows restoring an in-progress draft (if desired)
*/

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext.jsx';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../firebaseConfig.js';
import StepShell from '../../../components/StepShell.jsx';
import ProgressDots from '../../../components/ProgressDots.jsx';
import InfluencerAccountAuth from './steps/InfluencerAccountAuth.jsx';
import InfluencerAudience from './steps/InfluencerAudience.jsx';
import InfluencerBio from './steps/InfluencerBio.jsx';
import InfluencerPreview from './steps/InfluencerPreview.jsx';

const InfluencerWizard = ({ onNavigate, queryParams = '' }) => {
  const { user, profile, loading } = useAuth();
  
  // Wizard state management
  const [currentStep, setCurrentStep] = useState(0);
  const [wizardState, setWizardState] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Define step configurations
  const steps = [
    { 
      component: InfluencerAccountAuth, 
      title: 'Account Information',
      subtitle: 'Tell us about your personal brand!'
    },
    { 
      component: InfluencerAudience, 
      title: 'Audience & Reach',
      subtitle: 'Tell us about your audience!'
    },
    { 
      component: InfluencerBio, 
      title: 'Bio & Content',
      subtitle: 'Let\'s make your bio!'
    },
    { 
      component: InfluencerPreview, 
      title: 'Review & Submit',
      subtitle: 'Review your profile before creating it'
    }
  ];

  const totalSteps = steps.length;
  const currentStepConfig = steps[currentStep];

  // Handle fresh parameter - clear localStorage data
  useEffect(() => {
    const urlParams = new URLSearchParams(queryParams);
    const isFresh = urlParams.get('fresh') === '1';
    
    if (isFresh) {
      // Clear all wizard draft data
      localStorage.removeItem('ih.wizardDraft.preAuth');
      
      // Clear any existing uid-based drafts
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('ih.wizardDraft.')) {
          localStorage.removeItem(key);
        }
      });
      
      // Ensure we start at Step 0 with empty state
      setCurrentStep(0);
      setWizardState({});
    }
  }, [queryParams]); // Run when queryParams change

  // Redirect logic based on authentication and onboarding status
  useEffect(() => {
    if (!loading) {
      const urlParams = new URLSearchParams(queryParams);
      const isFresh = urlParams.get('fresh') === '1';
      
      // If user is authenticated and onboarding is complete, redirect to Profile Builder (unless fresh)
      if (user && profile && profile.onboardingComplete && !isFresh) {
        onNavigate('profile-edit');
        return;
      }
      
      // If user is authenticated but not an influencer, redirect to Profile Builder (unless fresh)
      if (user && profile && profile.userType !== 'influencer' && !isFresh) {
        onNavigate('profile-edit');
        return;
      }
      
      // If user is authenticated and is an influencer, load draft data (only if not fresh)
      if (user && profile && profile.userType === 'influencer' && !isFresh) {
        const savedData = localStorage.getItem(`ih.wizardDraft.${user.uid}`);
        if (savedData) {
          try {
            const parsedData = JSON.parse(savedData);
            setWizardState(parsedData);
            // Restore saved step if available, otherwise start at Step 0
            if (parsedData.currentStep !== undefined) {
              setCurrentStep(parsedData.currentStep);
            }
            // If no saved step, currentStep remains 0 (default)
          } catch (error) {
            console.error('Error loading wizard data:', error);
            // If error loading, currentStep remains 0 (default)
          }
        }
        // If no saved data, currentStep remains 0 (default)
      }
    }
  }, [user, profile, loading, onNavigate, queryParams]);

  // Load preAuth draft data if user is not authenticated (Step 1)
  useEffect(() => {
    if (!user) {
      const urlParams = new URLSearchParams(queryParams);
      const isFresh = urlParams.get('fresh') === '1';
      
      // Only load preAuth data if not in fresh mode
      if (!isFresh) {
        const preAuthData = localStorage.getItem('ih.wizardDraft.preAuth');
        if (preAuthData) {
          try {
            const parsedData = JSON.parse(preAuthData);
            setWizardState(parsedData);
            // Restore saved step if available, otherwise start at Step 0
            if (parsedData.currentStep !== undefined) {
              setCurrentStep(parsedData.currentStep);
            }
            // If no saved step, currentStep remains 0 (default)
          } catch (error) {
            console.error('Error loading preAuth data:', error);
            // If error loading, currentStep remains 0 (default)
          }
        }
        // If no preAuth data, currentStep remains 0 (default)
      }
    }
  }, [user, queryParams]);

  // Save wizard data to localStorage on every change
  useEffect(() => {
    if (user?.uid && Object.keys(wizardState).length > 0) {
      const dataToSave = { ...wizardState, currentStep };
      localStorage.setItem(`ih.wizardDraft.${user.uid}`, JSON.stringify(dataToSave));
    } else if (!user && Object.keys(wizardState).length > 0) {
      // Save to preAuth draft if user not authenticated yet
      const dataToSave = { ...wizardState, currentStep };
      localStorage.setItem('ih.wizardDraft.preAuth', JSON.stringify(dataToSave));
    }
  }, [wizardState, currentStep, user?.uid]);

  // Handle step data updates - memoized to prevent infinite loops
  const handleStepDataUpdate = useCallback((stepData) => {
    setWizardState(prevState => {
      const newState = {
        ...prevState,
        ...stepData
      };
      
      // Save to localStorage if user is authenticated
      if (user?.uid) {
        const dataToSave = {
          ...newState,
          currentStep
        };
        localStorage.setItem(`ih.wizardDraft.${user.uid}`, JSON.stringify(dataToSave));
      } else {
        // Save to preAuth draft if not authenticated
        const dataToSave = {
          ...newState,
          currentStep
        };
        localStorage.setItem('ih.wizardDraft.preAuth', JSON.stringify(dataToSave));
      }
      
      return newState;
    });
  }, [user?.uid, currentStep]);

  // Navigate to next step
  const handleNext = async () => {
    // For Step 1 (Account), the validation is already done in the child component
    // So we skip parent validation for Step 1 to avoid double validation
    if (currentStep !== 0) {
      // Validate current step - pass the full wizardState which should have all previous step data
      const validation = await currentStepConfig.component.validate?.(wizardState);
      if (validation && !validation.valid) {
        console.log('❌ Parent validation failed:', validation.errors);
        setError(validation.errors?.join(', ') || 'Please fix the errors above');
        return;
      }
    } else {
      console.log('✅ Skipping parent validation for Step 1 (already validated in child)');
    }

    // Special validation for moving to preview step (Step 3 -> Step 4)
    if (currentStep === 2) { // Step 3 (Bio) -> Step 4 (Preview)
      // Get the latest state - check localStorage as well in case state hasn't updated
      let latestState = wizardState;
      
      // Try to get the latest from localStorage if available
      try {
        const savedData = user?.uid 
          ? localStorage.getItem(`ih.wizardDraft.${user.uid}`)
          : localStorage.getItem('ih.wizardDraft.preAuth');
        if (savedData) {
          const parsed = JSON.parse(savedData);
          // Merge with current state to get the most up-to-date version
          latestState = { ...wizardState, ...parsed };
          // Remove currentStep from the state object
          delete latestState.currentStep;
        }
      } catch (e) {
        console.warn('Could not load from localStorage:', e);
      }
      
      const accountData = latestState.account || {};
      const audienceData = latestState.audience || {};
      const bioData = latestState.bio || {};
      const socialLinks = latestState.socialLinks || {};
      
      // Debug: Log the wizard state to help diagnose issues
      console.log('🔍 Validation check - wizardState:', {
        account: accountData,
        audience: audienceData,
        bio: bioData,
        socialLinks: socialLinks,
        fullState: latestState,
        rawWizardState: wizardState
      });
      
      // Check if account data exists and has required fields
      const hasAccountData = accountData.firstName && 
                            accountData.lastName && 
                            accountData.email && 
                            accountData.phone;
      
      if (!hasAccountData) {
        console.log('❌ Missing account data:', {
          firstName: accountData.firstName,
          lastName: accountData.lastName,
          email: accountData.email,
          phone: accountData.phone,
          accountObject: accountData,
          hasAccount: !!latestState.account
        });
        setError('Please complete all required fields in Step 1 before reviewing your profile.');
        setCurrentStep(0); // Go back to Step 1
        return;
      }
      
      // Convert totalFollowers to number if it's a string
      const totalFollowers = typeof audienceData.totalFollowers === 'string' 
        ? parseInt(audienceData.totalFollowers, 10) 
        : audienceData.totalFollowers;
      
      // Convert engagementRate to number/string check
      const engagementRate = audienceData.engagementRate;
      const hasEngagementRate = engagementRate !== undefined && 
                                engagementRate !== null && 
                                engagementRate !== '' &&
                                !isNaN(parseFloat(engagementRate));
      
      if (!totalFollowers || !audienceData.niches?.length || !hasEngagementRate) {
        console.log('❌ Missing audience data:', {
          totalFollowers: totalFollowers,
          niches: audienceData.niches,
          engagementRate: engagementRate,
          hasEngagementRate: hasEngagementRate,
          audienceObject: audienceData
        });
        setError('Please complete all required fields in Step 2 before reviewing your profile.');
        setCurrentStep(1); // Go back to Step 2
        return;
      }
      
      // Check for at least one social media profile
      const socialFields = ['instagram', 'tiktok', 'youtube', 'x', 'website'];
      const hasAtLeastOneSocial = socialFields.some(field => {
        const value = socialLinks[field];
        return value && value.trim().length > 0;
      });
      
      if (!hasAtLeastOneSocial) {
        console.log('❌ Missing social links:', socialLinks);
        setError('Please add at least one social media profile in Step 2 before reviewing your profile.');
        setCurrentStep(1); // Go back to Step 2
        return;
      }
      
      if (!bioData.text || bioData.text.trim().length === 0) {
        console.log('❌ Missing bio text:', bioData);
        setError('Please complete all required fields in Step 3 before reviewing your profile.');
        setCurrentStep(2); // Stay on Step 3
        return;
      }
      
      // All validations passed
      console.log('✅ All validations passed, proceeding to preview');
    }

    // Move to next step
    if (currentStep < totalSteps - 1) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      setError('');
      
      // Save the new step to localStorage
      if (user?.uid) {
        const dataToSave = {
          ...wizardState,
          currentStep: newStep
        };
        localStorage.setItem(`ih.wizardDraft.${user.uid}`, JSON.stringify(dataToSave));
      } else {
        const dataToSave = {
          ...wizardState,
          currentStep: newStep
        };
        localStorage.setItem('ih.wizardDraft.preAuth', JSON.stringify(dataToSave));
      }
    }
    // Note: Final submission is handled by InfluencerPreview component
  };

  // Navigate to previous step
  const handleBack = () => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      setError('');
      
      // Save the new step to localStorage
      if (user?.uid) {
        const dataToSave = {
          ...wizardState,
          currentStep: newStep
        };
        localStorage.setItem(`ih.wizardDraft.${user.uid}`, JSON.stringify(dataToSave));
      } else {
        const dataToSave = {
          ...wizardState,
          currentStep: newStep
        };
        localStorage.setItem('ih.wizardDraft.preAuth', JSON.stringify(dataToSave));
      }
    }
  };


  // Note: Final submission is now handled by InfluencerPreview component

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show error if not influencer (only if user is authenticated)
  if (user && profile && profile.userType !== 'influencer') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-600">This wizard is for influencers only.</p>
          <button 
            onClick={() => onNavigate('profile-edit')}
            className="mt-4 btn-primary"
          >
            Go to Profile Builder
          </button>
        </div>
      </div>
    );
  }

  // Render current step
  const CurrentStepComponent = currentStepConfig.component;
  const isLastStep = currentStep === totalSteps - 1;
  const isFirstStep = currentStep === 0;
  const isPreviewStep = currentStep === totalSteps - 1; // Preview step handles its own navigation

  // Create footer buttons based on step
  // Preview step handles its own CTA buttons, so no footer buttons needed
  const footerLeft = !isFirstStep && !isPreviewStep ? (
    <button
      type="button"
      onClick={handleBack}
      disabled={isSubmitting}
      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
    >
      Back
    </button>
  ) : null;

  const footerRight = !isFirstStep && !isPreviewStep ? (
    <button
      type="button"
      onClick={handleNext}
      disabled={isSubmitting}
      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
    >
      Continue
    </button>
  ) : null;

  return (
    <StepShell
      title={currentStepConfig.title}
      subtitle={currentStepConfig.subtitle}
      progressDots={{ current: currentStep, total: totalSteps }}
      footerLeft={footerLeft}
      footerRight={footerRight}
    >
      {/* Error display */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Step content */}
      <CurrentStepComponent
        data={wizardState}
        onDataChange={handleStepDataUpdate}
        onNext={handleNext}
        onBack={handleBack}
        onNavigate={onNavigate}
        user={user}
      />
    </StepShell>
  );
};

export default InfluencerWizard;

